import { useCallback, useEffect, useState } from 'react'
import { api } from '../lib/api'
import { getAdminFcmToken, listenForAdminPush } from '../lib/firebase'
import type { MessagePayload } from 'firebase/messaging'
import orderTone from '../assets/order.mp3'

type PushState = 'idle' | 'enabled' | 'blocked' | 'unsupported' | 'error'
export type AdminPushPayload = {
  title: string
  message: string
  url?: string
  orderId?: string
  notificationId?: string
  notificationType?: string
}

let soundUnlocked = false
const channelName = 'seasonly-admin-notifications'
const soundMutedKey = 'adminSoundMuted'
const soundEnabledKey = 'adminSoundEnabled'
const soundChangeEvent = 'seasonly-admin-sound-change'

export function isAdminNotificationSoundEnabled() {
  return localStorage.getItem(soundEnabledKey) === '1' && !isAdminNotificationSoundMuted()
}

export function isAdminNotificationSoundMuted() {
  return localStorage.getItem(soundMutedKey) === '1'
}

export function setAdminNotificationSoundMuted(muted: boolean) {
  localStorage.setItem(soundMutedKey, muted ? '1' : '0')
  if (muted) localStorage.setItem(soundEnabledKey, '0')
  window.dispatchEvent(new Event(soundChangeEvent))
}

export async function unlockNotificationSound() {
  setAdminNotificationSoundMuted(false)
  const audio = new Audio(orderTone)
  audio.volume = 0
  try {
    await audio.play()
    audio.pause()
    audio.currentTime = 0
    soundUnlocked = true
    localStorage.setItem(soundEnabledKey, '1')
    window.dispatchEvent(new Event(soundChangeEvent))
    return true
  } catch {
    localStorage.setItem(soundEnabledKey, '0')
    window.dispatchEvent(new Event(soundChangeEvent))
    return false
  }
}

export function playNotificationSound() {
  if (!isAdminNotificationSoundEnabled()) return
  const audio = new Audio(orderTone)
  audio.volume = 0.9
  audio.currentTime = 0
  audio.play().catch(() => {
    if (soundUnlocked) playFallbackTone()
  })
}

function playFallbackTone() {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext
  if (!AudioContextClass) return

  const context = new AudioContextClass()
  const oscillator = context.createOscillator()
  const gain = context.createGain()

  oscillator.type = 'sine'
  oscillator.frequency.setValueAtTime(880, context.currentTime)
  oscillator.frequency.setValueAtTime(660, context.currentTime + 0.12)
  gain.gain.setValueAtTime(0.001, context.currentTime)
  gain.gain.exponentialRampToValueAtTime(0.18, context.currentTime + 0.02)
  gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.32)

  oscillator.connect(gain)
  gain.connect(context.destination)
  oscillator.start()
  oscillator.stop(context.currentTime + 0.34)
}

export async function registerAdminPushToken() {
  const token = await getAdminFcmToken()
  if (!token) throw new Error('Firebase did not return a push token.')

  await api.post('/admin/notifications/push-token', { token })
  localStorage.setItem('adminFcmToken', token)
  return token
}

function payloadToAdminPush(payload: MessagePayload): AdminPushPayload {
  return {
    title: payload.notification?.title || payload.data?.title || 'Seasonly Admin',
    message: payload.notification?.body || payload.data?.body || 'New admin notification',
    url: payload.data?.url,
    orderId: payload.data?.orderId,
    notificationId: payload.data?.notificationId,
    notificationType: payload.data?.type,
  }
}

function shouldAlertAdminPush(payload: AdminPushPayload) {
  const title = payload.title.toLowerCase()
  return payload.notificationType === 'broadcast' ||
    title.includes('new order received') ||
    title.includes('new order rating') ||
    title.includes('feedback')
}

export function useAdminPushNotifications(onReceived?: (payload: AdminPushPayload) => void) {
  const [state, setState] = useState<PushState>('idle')
  const [error, setError] = useState('')

  const enablePush = useCallback(async () => {
    setError('')
    try {
      await registerAdminPushToken()
      setState('enabled')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to enable push notifications.'
      if (message.toLowerCase().includes('support')) setState('unsupported')
      else if ('Notification' in window && Notification.permission === 'denied') setState('blocked')
      else setState('error')
      setError(message)
    }
  }, [])

  useEffect(() => {
    if (!('Notification' in window)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState('unsupported')
      return
    }
    if (Notification.permission === 'granted' && localStorage.getItem('adminFcmToken')) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState('enabled')
    } else if (Notification.permission === 'denied') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState('blocked')
    }
  }, [])

  useEffect(() => {
    let unsubscribe: (() => void) | undefined
    const channel = 'BroadcastChannel' in window ? new BroadcastChannel(channelName) : null
    const handleBroadcast = (event: MessageEvent<{ type: string }>) => {
      if (event.data?.type === 'seasonly-admin-push') playNotificationSound()
    }
    const handleStorage = (event: StorageEvent) => {
      if (!channel && event.key === 'seasonlyAdminLastPushAt' && event.newValue) playNotificationSound()
    }

    channel?.addEventListener('message', handleBroadcast)
    window.addEventListener('storage', handleStorage)

    listenForAdminPush((payload) => {
      const adminPayload = payloadToAdminPush(payload)
      const shouldAlert = shouldAlertAdminPush(adminPayload)
      if (shouldAlert) {
        playNotificationSound()
        channel?.postMessage({ type: 'seasonly-admin-push' })
        localStorage.setItem('seasonlyAdminLastPushAt', String(Date.now()))
      }
      onReceived?.(adminPayload)

      if (shouldAlert && Notification.permission === 'granted') {
        new Notification(adminPayload.title, {
          body: adminPayload.message,
          icon: '/favicon.svg',
          tag: payload.data?.tag || 'seasonly-admin-foreground',
        })
      }
    }).then((unsub) => {
      unsubscribe = unsub
    }).catch(() => undefined)

    return () => {
      channel?.removeEventListener('message', handleBroadcast)
      channel?.close()
      window.removeEventListener('storage', handleStorage)
      unsubscribe?.()
    }
  }, [onReceived])

  useEffect(() => {
    if (soundUnlocked) return undefined
    if (localStorage.getItem(soundEnabledKey) !== '1') return undefined

    const unlockFromGesture = () => {
      unlockNotificationSound()
    }

    window.addEventListener('pointerdown', unlockFromGesture, { once: true })
    return () => window.removeEventListener('pointerdown', unlockFromGesture)
  }, [])

  return { state, error, enablePush }
}

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext
  }
}
